/**
 * Billing across every client (`docs/28_UNIFIED_BILLING.md` §8): the wallet's
 * rules, the reconciliation of Connect's books with the SMS vendor's, and
 * revenue against what the vendor charged.
 *
 * Reconciliation reports disagreements; it never corrects a balance. A fall
 * in a vendor account that the reported costs do not explain is a send Connect
 * did not make — somebody using the vendor's own portal — or a load nobody
 * recorded; parts the network counted differently from Connect, or an account
 * that no longer covers the credits outstanding, are flagged the same way.
 * Staff only: none of it reaches a client.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  QueryPending,
  StatusChip,
  Tabs,
  api,
  formatMoney,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { refusalText } from './api-refusal';
import { formatMetered } from './ai-card';
import { SERVICE_LABELS, billingKeys, type WalletService } from './billing-card';

interface Reconciliation {
  connections: {
    client: { public_id: string; name: string };
    connection: { public_id: string; label: string | null };
    messages: number;
    parts: number;
    reported_parts: number;
    reported_messages: number;
    provider_cost: string;
    rated_messages: number;
    readings: number;
    balance_fall: string | null;
    loads: string;
    unexplained: string | null;
    vendor_balance: string | null;
    outstanding_credits: number;
    covered: boolean | null;
    issues: ('parts' | 'balance' | 'capacity')[];
  }[];
  ledgers: { client: string | null; ledger: string; balance: string | number; entries: string | number }[];
}

interface Revenue {
  rows: {
    client: { public_id: string; name: string };
    service: WalletService;
    charged: string;
    credits_sold: number;
    credits_sold_amount: string;
    credits_used: number;
    credits_used_value: string;
    revenue: string;
    provider_cost: string | null;
    margin: string | null;
  }[];
}

const ISSUE_LABELS = { parts: 'Parts differ', balance: 'Fall not explained', capacity: 'Vendor short' };

export function BillingPage() {
  const [tab, setTab] = useState('reconciliation');
  return (
    <div className="space-y-4">
      <PageHeader
        title="Billing"
        description="Every client's wallet and SMS credits against the vendors' books, and what Connect earned. Staff only."
        tabs={
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { id: 'reconciliation', label: 'Reconciliation' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'rules', label: 'Rules' },
            ]}
          />
        }
      />
      {tab === 'reconciliation' ? <ReconciliationTab /> : tab === 'revenue' ? <RevenueTab /> : <RulesTab />}
    </div>
  );
}

function ReconciliationTab() {
  const report = useQuery({
    queryKey: [...billingKeys.all, 'reconciliation'],
    queryFn: () => api.get<Reconciliation>('/billing/reconciliation'),
  });
  if (report.isLoading) return <LoadingState label="Reconciling" />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  const data = report.data;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="SMS sold in credits — last 30 days"
          description="Connect's count of every accepted message, the vendor's reports, and the vendor account's balance."
        />
        {data.connections.length === 0 ? (
          <CardBody>
            <EmptyState title="No SMS is sold in credits yet" />
          </CardBody>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs font-medium text-muted">
              <tr>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2 text-right">Parts (ours / vendor's)</th>
                <th className="px-3 py-2 text-right">Vendor cost</th>
                <th className="px-3 py-2 text-right">Balance fall</th>
                <th className="px-3 py-2 text-right">Not explained</th>
                <th className="px-3 py-2 text-right">Credits out</th>
                <th className="px-3 py-2">Issues</th>
              </tr>
            </thead>
            <tbody>
              {data.connections.map((row) => (
                <tr key={row.connection.public_id} className="border-t border-line">
                  <td className="px-3 py-2">
                    {row.client.name}
                    {row.connection.label ? <span className="text-xs text-muted"> · {row.connection.label}</span> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {formatNumber(row.parts)} / {formatNumber(row.reported_parts)}
                    <span className="block text-2xs text-muted">
                      {formatNumber(row.reported_messages)} of {formatNumber(row.messages)} reported
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular">{formatMetered(row.provider_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular">
                    {row.balance_fall ? formatMetered(row.balance_fall) : `${row.readings} reading(s)`}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular">
                    {row.unexplained ? formatMetered(row.unexplained) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatNumber(row.outstanding_credits)}</td>
                  <td className="px-3 py-2">
                    {row.issues.length === 0 ? (
                      <StatusChip value="ok" labels={{ ok: 'Agrees' }} tones={{ ok: 'success' }} />
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {row.issues.map((issue) => (
                          <StatusChip key={issue} value={issue} labels={ISSUE_LABELS} tones={{ [issue]: 'danger' }} />
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card>
        <CardHeader
          title="Ledgers"
          description="Every wallet and credit account equals the sum of its entries. Anything listed here does not — a person must look."
        />
        <CardBody>
          {data.ledgers.length === 0 ? (
            <p className="text-sm text-muted">Every ledger adds up.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.ledgers.map((row) => (
                <li key={`${row.client}-${row.ledger}`} className="text-danger">
                  {row.client} — {row.ledger}: balance {String(row.balance)}, entries {String(row.entries)}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function RevenueTab() {
  const report = useQuery({
    queryKey: [...billingKeys.all, 'revenue'],
    queryFn: () => api.get<Revenue>('/billing/revenue'),
  });
  if (report.isLoading) return <LoadingState label="Adding it up" />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  return (
    <Card>
      <CardHeader
        title="Last 30 days"
        description="Credits are earned as they are used, at the average price the client paid for them. Margin is shown where the vendor's cost is known."
      />
      {report.data.rows.length === 0 ? (
        <CardBody>
          <EmptyState title="Nothing charged in this period" />
        </CardBody>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs font-medium text-muted">
            <tr>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Service</th>
              <th className="px-3 py-2 text-right">Credits sold</th>
              <th className="px-3 py-2 text-right">Credits used</th>
              <th className="px-3 py-2 text-right">Revenue</th>
              <th className="px-3 py-2 text-right">Vendor cost</th>
              <th className="px-3 py-2 text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {report.data.rows.map((row) => (
              <tr key={`${row.client.public_id}-${row.service}`} className="border-t border-line">
                <td className="px-3 py-2">{row.client.name}</td>
                <td className="px-3 py-2">{SERVICE_LABELS[row.service]}</td>
                <td className="px-3 py-2 text-right tabular">
                  {row.credits_sold ? `${formatNumber(row.credits_sold)} · ${formatMoney(row.credits_sold_amount)}` : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular">
                  {row.credits_used ? formatNumber(row.credits_used) : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular">{formatMoney(row.revenue)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs tabular">
                  {row.provider_cost ? formatMetered(row.provider_cost) : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular">{row.margin ? formatMoney(row.margin) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** The wallet's rules (`billing.wallet`): saved whole, so no rule is reset by
 * saving another. */
interface WalletRules {
  minimum_top_up: string;
  /** The plan a client starts on when its SMS moves to credits. */
  default_price_per_credit: string;
  default_provider_cost_per_credit: string;
}

function RulesTab() {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.billing.edit');
  const rules = useQuery({
    queryKey: [...billingKeys.all, 'rules'],
    queryFn: () => api.get<WalletRules>('/billing/rules'),
  });
  const [draft, setDraft] = useState<Partial<WalletRules>>({});
  const save = useMutation({
    mutationFn: (value: WalletRules) => api.put<WalletRules>('/billing/rules', value),
    onSuccess: (data) => {
      queryClient.setQueryData([...billingKeys.all, 'rules'], data);
      setDraft({});
      toast.success('Saved');
    },
    onError: (e) => toast.error('Not saved', refusalText(e)),
  });
  if (rules.data === undefined) return <QueryPending query={rules} label="Loading the rules" />;
  const value = { ...rules.data, ...draft };
  const field = (key: keyof WalletRules) => ({
    inputMode: 'decimal' as const,
    disabled: !canEdit,
    value: value[key],
    onChange: (e: { target: { value: string } }) => setDraft({ ...draft, [key]: e.target.value }),
  });
  return (
    <Card>
      <CardHeader title="The wallet's rules" description="The same for every client." />
      <CardBody className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Smallest top-up (₹)"
            hint="A smaller credit — a trial, a correction — is made as an adjustment."
            {...field('minimum_top_up')}
          />
          <Input
            label="Default price a credit (₹)"
            hint="The fixed price a client starts on when its SMS moves to credits. Each client's plan is its own after."
            {...field('default_price_per_credit')}
          />
          <Input
            label="Vendor's cost a credit (₹)"
            hint="What the vendor charges per SMS part: how a vendor balance becomes credits."
            {...field('default_provider_cost_per_credit')}
          />
        </div>
        {canEdit && Object.keys(draft).length > 0 ? (
          <Button loading={save.isPending} onClick={() => save.mutate(value)}>
            Save
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );
}
